import {
  Flex,
  Heading,
  Icon,
  Text as ChakraText,
  useClipboard,
  HStack,
  VStack,
  StackDivider,
  Spacer,
  chakra,
  Link,
  Center,
  Spinner,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiProfileLine, RiCoinsLine } from 'react-icons/ri'
import { IoMdPaperPlane } from 'react-icons/io'
import { MdContentCopy } from 'react-icons/md'

import useFetch, { CachePolicies } from 'use-http'
import { FaLongArrowAltRight } from 'react-icons/fa'
import { useDisclosure } from '@chakra-ui/hooks'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import { BaseLayout } from '..'
import type { CreditActivityResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import useToast from '../../hooks/useToast'
import SupportRequest from '../../components/Referrals/SupportRequest'

const ReferralsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const { user } = useContext(UserContext)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [data, setData] = useState<CreditActivityResponse>()

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = (await request.get(
        `/api/clients/${user.clientNumber}/credit/activity`,
      )) as CreditActivityResponse
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }
  const { onCopy } = useClipboard(`${data?.coupon.url}`)
  useEffect(() => {
    void getData()
  }, [user])

  const HeadingSmall = chakra('h6', {
    baseStyle: {
      display: 'flex',
      fontSize: 'md',
      fontWeight: 'semibold',
      color: 'appDarkIndigo',
    },
  })

  const Circle = chakra('div', {
    baseStyle: {
      padding: '20px',
      marginBottom: '1.8rem',
      width: '5.625rem',
      height: '5.625rem',
      borderRadius: 'full',
      backgroundColor: 'appPaleGrey',
    },
  })

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading as="h5" size="md" fontWeight="semibold" color="appDarkIndigo">
          {t('referrals_and_coupons', 'Referrals and coupons')}
        </Heading>
      </MainContentHeadingBox>
      <MainContentWrapper>
        <Flex w="100%" justifyContent="center" alignContent="center">
          <VStack p={2}>
            <Circle>
              <Icon as={IoMdPaperPlane} boxSize={12} color="appBlue" />
            </Circle>
            <HeadingSmall>{t('send_referral_code')}</HeadingSmall>
            <ChakraText marginTop="1rem" textAlign="center" pl={5} pr={5}>
              {t('send_referral_code_info')}
            </ChakraText>
          </VStack>
          <Flex>
            <Icon
              as={FaLongArrowAltRight}
              boxSize={12}
              position="relative"
              top={8}
              color="appGrey"
            />
          </Flex>

          <VStack p={2}>
            <Circle>
              <Icon as={RiProfileLine} boxSize={12} color="appBlue" />
            </Circle>
            <HeadingSmall>{t('registration')}</HeadingSmall>
            <ChakraText textAlign="center" pl={5} pr={5}>
              {t('registration_info')}
            </ChakraText>
          </VStack>
          <Flex>
            <Icon
              as={FaLongArrowAltRight}
              boxSize={12}
              position="relative"
              top={8}
              color="appGrey"
            />
          </Flex>

          <VStack p={2}>
            <Circle>
              <Icon as={RiCoinsLine} boxSize={12} color="appBlue" />
            </Circle>
            <HeadingSmall>{t('earn_credit_points')}</HeadingSmall>
            <ChakraText textAlign="center" pl={5} pr={5}>
              {t('earn_credit_points_info')}
            </ChakraText>
          </VStack>
        </Flex>

      {
        !data || loading ? (
          <Center width="full" paddingTop={14}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor="appPaleGrey"
              color="appBlue"
              size="xl"
            />
          </Center>
        ) : (
          <HStack
            paddingTop={14}
            paddingRight={8}
            paddingLeft={8}
            divider={<StackDivider borderColor="gray.200" alignSelf="stretch" />}
            alignItems="flex-start"
            spacing={4}
          >
            <VStack
              h="100%"
              w="100%"
              paddingRight={20}
              spacing={9}
              position="relative"
              mt={14}
              mb={12}
            >
              <Flex w="100%">
                <Heading
                  as="h5"
                  size="md"
                  fontWeight="semibold"
                  color="appDarkIndigo"
                >
                  {t('total_available_and_pending_credits')}
                </Heading>

                <Spacer />
                <Heading
                  as="h5"
                  size="md"
                  fontWeight="semibold"
                  color="appDarkIndigo"
                >
                  $
                  {Number(data?.stats.issuedAmount) +
                    Number(data?.stats.pendingAmount)}
                </Heading>
              </Flex>
              <Flex w="100%">
                <Flex direction="column">
                  <HeadingSmall>
                    {t('total_registration_using_your_referral_code')}
                  </HeadingSmall>
                  <ChakraText>
                    {t('total_registration_using_your_referral_code_info')}
                  </ChakraText>
                </Flex>
                <Spacer />
                <ChakraText fontWeight="semibold">
                  {Number(data?.stats.signUpCount)}
                </ChakraText>
              </Flex>
              <Flex w="100%">
                <Flex direction="column">
                  <HeadingSmall>
                    {t('possible_credits_to_be_earned')}
                  </HeadingSmall>
                  <ChakraText>
                    {t('possible_credits_to_be_earned_info')}
                  </ChakraText>
                </Flex>
                <Spacer />
                <ChakraText fontWeight="semibold">
                  ${data?.stats.pendingAmount}
                </ChakraText>
              </Flex>
              <Flex w="100%">
                <Flex direction="column">
                  <HeadingSmall>{t('available_credit')}</HeadingSmall>
                  <ChakraText>{t('available_credit_info')}</ChakraText>
                </Flex>
                <Spacer />
                <ChakraText fontWeight="semibold">
                  ${data?.stats.issuedAmount}
                </ChakraText>
              </Flex>
              <Flex w="100%">
                <Flex direction="column">
                  <HeadingSmall>{t('used_credits')}</HeadingSmall>
                  <ChakraText>{t('used_credits_info')}</ChakraText>
                </Flex>
                <Spacer />
                <ChakraText fontWeight="semibold">
                  ${data?.stats.appliedAmount}
                </ChakraText>
              </Flex>
            </VStack>
            <VStack
              paddingLeft={10}
              h="100%"
              w="100%"
              spacing={4}
              position="relative"
              mt={12}
              mb={12}
              alignItems="left"
            >
              <Flex w="100%" direction="row" alignItems="center">
                <Heading
                  as="h5"
                  size="md"
                  fontWeight="semibold"
                  color="appDarkIndigo"
                  pr={4}
                >
                  {t('your_referral_code')}
                </Heading>
                <Spacer />
                <Flex
                  paddingX={2}
                  paddingY={3}
                  borderRadius="base"
                  border="dashed 1px"
                  backgroundColor="appPaleGrey"
                  borderColor="appClearBlue"
                >
                  <Heading
                    as="h5"
                    size="md"
                    fontWeight="semibold"
                    paddingRight={12}
                    color="appDarkIndigo"
                  >
                    {data?.coupon.code}
                  </Heading>
                  <Spacer />
                  <Icon
                    as={MdContentCopy}
                    boxSize={5}
                    color="appBlue"
                    onClick={() => {
                      onCopy()
                      toast({
                        description: t('copied', 'Copied!'),
                        status: 'success',
                        isClosable: true,
                        duration: 5000,
                        position: 'top-right',
                        variant: 'outline',
                      })
                    }}
                    cursor="pointer"
                  />
                </Flex>
              </Flex>

              <HeadingSmall>{t('twenty_credit_per_sign')}</HeadingSmall>
              <ChakraText>
                {t(
                  'referral_code_info',
                  `Refer others to use ImageKit. You get $20 credit, they get $10 credit.
                  Get your friends to sign up on ImageKit using this code. Each friend that you 
                  refer gets $10 credits. Once they pay their first bill on ImageKit, 
                  you get $20 credits. There is no limit on the credits you can earn.`,
                )}
              </ChakraText>

              <HeadingSmall>{t('invite_your_friends')}</HeadingSmall>

              <ChakraText>
                {t('help_info')}
                {user?.pricingPlan.isPaidPlan ? (
                  <Link
                    fontSize="1em"
                    onClick={() => {
                      window.Intercom(
                        'showNewMessage',
                        t(
                          'referral_support_intercom_text',
                          `Hi\n\nI wanted to know more about ImageKit.io's
                        referral program.\nHow do I earn the $20 credit?`,
                        ).toString(),
                      )
                    }}
                  >
                    {t('contact_us', 'contact us.')}.
                  </Link>
                ) : (
                  <Link lineHeight="base" onClick={onOpen}>
                    {t('contact_us', 'contact us.')}.
                  </Link>
                )}
              </ChakraText>

              <SupportRequest isOpen={isOpen} onClose={onClose} />
            </VStack>
          </HStack>
        )
      }
      </MainContentWrapper>
    </BaseLayout>
  )
}

export default ReferralsLayout
